import React, { useEffect, useMemo, useCallback, memo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, ButtonToolbar, DropdownButton, FormControl, Glyphicon, InputGroup, MenuItem } from 'react-bootstrap';
import qs from 'qs';
import { isEqual, pickBy } from 'lodash';

import { useReports } from '../../index';
import { useWindowSize } from '../../../../../utils/hooks/useWindowSize';
import { subtractYears } from '../../../../../utils/helper';

import SubMenuWithDate from '../../../Payments/Chart/components/Filters/SubMenuWithDate';
import MultiSelect from '../../../../../components/Select/MultiSelect';

const Filters = () => {
  const location = useLocation();
  const history = useHistory();
  const { filters, config } = useReports();
  const { width } = useWindowSize();

  const queryString = useMemo(
    () => qs.parse(location.search.slice(1)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search],
  );

  const baseQuery = useMemo(() => {
    const { page, stack, ...rest } = queryString;
    return rest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const [dateFilter, setDateFilter] = useState({
    range: filters.range,
    date_from: filters.date_from,
    date_to: filters.date_to,
  });
  const [search, setSearch] = useState(filters.query || '');
  const [sources, setSources] = useState(filters.source || []);
  const [showDrop, setShowDrop] = useState(false);

  const minDate = useMemo(() => subtractYears(null, 15), []);

  useEffect(() => {
    setSources(filters.source || []);
    setSearch(filters.query || '');
    setDateFilter((prev) => ({
      ...prev,
      range: filters.range,
      date_from: filters.date_from,
      date_to: filters.date_to,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.range, filters.date_from, filters.date_to]);

  useEffect(() => {
    const queryValue = baseQuery.query ?? '';
    if (queryValue !== search) {
      setSearch(queryValue);
    }

    const currentSources = baseQuery.source ? baseQuery.source.map(Number) : [];
    if (!isEqual(currentSources, sources)) {
      setSources(currentSources);
    }

    if (baseQuery.hasOwnProperty('range') && baseQuery.range !== dateFilter.range) {
      setDateFilter((prevState) => ({
        ...prevState,
        range: baseQuery.range,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery.query, baseQuery.source, baseQuery.range]);

  const onSubmitSearch = useCallback((e) => {
    e.preventDefault();
    history.push({
      pathname: location.pathname,
      search: qs.stringify(pickBy({ ...baseQuery, query: search })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery, search]);

  const activeFilterTitle = useMemo(() => {
    if (!config?.date_range_filter?.length) return 'Today';

    const activeRange = config.date_range_filter.find(
      (item) => item.code === dateFilter.range,
    );
    return activeRange?.title || config.date_range_filter[0].title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter.range, config?.date_range_filter]);

  const onChangeRange = useCallback((selectedRange, selectedDateFrom = null, selectedDateTo = null) => {
    setDateFilter((prev) => ({
      ...prev,
      range: selectedRange,
      date_from: selectedDateFrom,
      date_to: selectedDateTo,
    }));

    history.push({
      pathname: location.pathname,
      search: qs.stringify(pickBy({
        ...baseQuery,
        range: selectedRange,
        date_from: selectedDateFrom,
        date_to: selectedDateTo,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery]);

  const sourceOptions = useMemo(() => config?.sources?.map((item) => ({
    id: item.id,
    label: `${item.label}${item.hasOwnProperty('count') ? ` (${item.count})` : ''}`,
    count: item.count || 0,
    value: item.id,
  })) || [],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [config?.sources]);

  const onChangeSource = useCallback((options) => {
    setSources(options);
    history.push({
      pathname: location.pathname,
      search: qs.stringify(pickBy({ ...baseQuery, source: options })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery]);

  return (
    <div className="reports-filters__items">
      <div className="reports-filters__items-block">
        <ButtonToolbar>
          <DropdownButton
            onToggle={setShowDrop}
            open={showDrop}
            pullRight={width > 767}
            bsStyle="default"
            title={activeFilterTitle}
            id="reports-dropdown-selectrange"
          >
            {config?.date_range_filter?.map((item) => (
              item.code === 'custom' ? (
                <SubMenuWithDate
                  key={item.code}
                  element={item}
                  pullRight={width > 767}
                  filters={dateFilter.range === 'custom' && (dateFilter.date_from && dateFilter.date_to) ? {
                    date_range_filter_settings: {
                      default_from: dateFilter.date_from,
                      default_to: dateFilter.date_to,
                    },
                  } : null}
                  minDate={config?.date_range_filter_settings?.min_date || minDate}
                  maxDate={dateFilter.date_to}
                  onSelect={(...dateValues) => {
                    const [range, dates] = dateValues;
                    onChangeRange(range, dates.startDate, dates.endDate)
                  }}
                />
              ) : (
                <MenuItem
                  key={item.code}
                  active={item.code === dateFilter.range}
                  eventKey={item.code}
                  onSelect={(newRange) => onChangeRange(newRange, null, null)}
                >
                  {item.title}
                </MenuItem>
              )
            ))}
          </DropdownButton>
        </ButtonToolbar>
      </div>

      <div className="reports-filters__items-block">
        <MultiSelect
          title={sources.length ? `Sources (${sources.length})` : 'All sources'}
          options={sourceOptions}
          values={sources}
          onChange={onChangeSource}
        />
      </div>

      <form onSubmit={onSubmitSearch}>
        <InputGroup>
          <FormControl
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search username"
            type="text"
          />
          <InputGroup.Button>
            <Button type="submit">
              <Glyphicon glyph="search" />
            </Button>
          </InputGroup.Button>
        </InputGroup>
      </form>
    </div>
  );
};

export default memo(Filters);
